import React, { useState, useEffect } from 'react';
import { PostackCMS, Post, PostsResponse, GetPostsOptions } from './client';

export interface PostackCMSConfig {
  baseUrl: string;
  apiKey: string;
}

export interface BlogListOptions {
  postsPerPage?: number;
  showFeatured?: boolean;
  layout?: 'grid' | 'list';
  showImages?: boolean;
  showExcerpt?: boolean;
  showAuthor?: boolean;
  showDate?: boolean;
  showTags?: boolean;
  className?: string;
}

export interface BlogListProps {
  config: PostackCMSConfig;
  options?: BlogListOptions;
  onPostClick?: (post: Post) => void;
}

export const BlogList: React.FC<BlogListProps> = ({ 
  config, 
  options = {}, 
  onPostClick 
}) => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const {
    postsPerPage = 10,
    showFeatured = false,
    layout = 'grid',
    showImages = true,
    showExcerpt = true,
    showAuthor = true,
    showDate = true,
    showTags = true,
    className = ''
  } = options;

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        setError(null);

        const cms = new PostackCMS(config);
        const getPostsOptions: GetPostsOptions = {
          published: true,
          limit: postsPerPage,
          page,
        };

        if (showFeatured) {
          getPostsOptions.featured = true;
        }

        const response: PostsResponse = await cms.getPosts(getPostsOptions);
        setPosts(response.posts);
      } catch (err) {
        console.error('Error fetching posts:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch posts');
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [config, showFeatured, postsPerPage, page]);

  if (loading) {
    return (
      <div className={`postack-loading ${className}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(postsPerPage)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow-md p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded mb-4"></div>
              <div className="h-6 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 bg-gray-200 rounded mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`postack-error ${className}`}>
        <div className="text-center py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="text-red-800 font-medium mb-2">Error Loading Posts</h3>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className={`postack-empty ${className}`}>
        <div className="text-center py-8">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-blue-800 font-medium mb-2">No Posts Found</h3>
            <p className="text-blue-600 text-sm">
              Create some posts in your CMS dashboard to see them here!
            </p>
          </div>
        </div>
      </div>
    );
  }

  const gridClass = layout === 'grid' 
    ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
    : 'space-y-6';

  return (
    <div className={`postack-blog-list ${gridClass} ${className}`}>
      {posts.map((post) => {
        // Check for both image and featuredImage fields
        const postImage = post.image || post.featuredImage;
        
        return (
          <article 
            key={post.id} 
            className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => onPostClick?.(post)}
          >
            {showImages && postImage && (
              <div className="aspect-video bg-gray-200">
                <img 
                  src={postImage} 
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div className="p-6">
              {showDate && (
                <div className="flex items-center text-sm text-gray-500 mb-2">
                  <span>{new Date(post.createdAt).toLocaleDateString()}</span>
                  {showAuthor && post.author && (
                    <>
                      <span className="mx-2">•</span>
                      <span>{post.author.name}</span>
                    </>
                  )}
                </div>
              )}
              <h3 className="text-xl font-semibold text-gray-900 mb-2 line-clamp-2">
                {post.title}
              </h3>
              {showExcerpt && (
                <p className="text-gray-600 mb-4 line-clamp-3">
                  {post.excerpt || post.content?.substring(0, 150) + '...'}
                </p>
              )}
              <div className="flex items-center justify-between">
                <span className="text-blue-600 hover:text-blue-800 font-medium text-sm">
                  Read More →
                </span>
                {showTags && post.tags && post.tags.length > 0 && (
                  <div className="flex gap-1">
                    {post.tags.slice(0, 2).map((tag) => (
                      <span 
                        key={tag.id} 
                        className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded"
                      >
                        {tag.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
};

export interface FeaturedPostsProps {
  config: PostackCMSConfig;
  limit?: number;
  className?: string;
  onPostClick?: (post: Post) => void;
}

export const FeaturedPosts: React.FC<FeaturedPostsProps> = ({ 
  config, 
  limit = 3, 
  className = '',
  onPostClick 
}) => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFeaturedPosts = async () => {
      try {
        setLoading(true);
        setError(null);

        const cms = new PostackCMS(config);
        const response: PostsResponse = await cms.getFeaturedPosts(limit);
        setPosts(response.posts);
      } catch (err) {
        console.error('Error fetching featured posts:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch featured posts');
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedPosts();
  }, [config, limit]);

  if (loading) {
    return (
      <div className={`postack-featured-loading ${className}`}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(limit)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow-md p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded mb-4 w-1/3"></div>
              <div className="h-6 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 bg-gray-200 rounded mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`postack-featured-error ${className}`}>
        <div className="text-center py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="text-red-800 font-medium mb-2">Error Loading Featured Posts</h3>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className={`postack-featured-empty ${className}`}>
        <div className="text-center py-8">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-blue-800 font-medium mb-2">No Featured Posts</h3>
            <p className="text-blue-600 text-sm">
              Mark some posts as featured in your CMS dashboard to see them here!
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`postack-featured-posts grid grid-cols-1 md:grid-cols-3 gap-6 ${className}`}>
      {posts.map((post) => (
        <article 
          key={post.id} 
          className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => onPostClick?.(post)}
        >
          <div className="mb-3">
            <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">
              {post.category?.name || 'Featured'}
            </span>
          </div>
          
          <h3 className="text-xl font-semibold text-gray-900 mb-2 line-clamp-2">
            {post.title}
          </h3>
          
          <div className="flex items-center text-sm text-gray-500 mb-3">
            <span>
              {new Date(post.createdAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              })}
            </span>
            {post.author && (
              <>
                <span className="mx-2">•</span>
                <span>by {post.author.name}</span>
              </>
            )}
          </div>
          
          <p className="text-gray-600 mb-4 line-clamp-3">
            {post.excerpt || post.content?.substring(0, 120) + '...'}
          </p>
          
          <div className="mt-4">
            <span className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium text-sm">
              Read more
              <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </div>
        </article>
      ))}
    </div>
  );
}; 